import React from 'react';
import { Calendar, Clock, MapPin, User, Star, CheckCircle, XCircle, AlertCircle, Hash } from 'lucide-react';
import { format } from 'date-fns';
import TagDisplay from './TagDisplay';

const BookingCard = ({ booking, showActions = true }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'requested': return 'badge-warning';
      case 'accepted': return 'badge-success';
      case 'rejected': return 'badge-error';
      case 'in_progress': return 'badge-info';
      case 'completed': return 'badge-success';
      case 'cancelled': return 'badge-error';
      case 'disputed': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'requested': return <Clock className="w-4 h-4" />;
      case 'accepted': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'in_progress': return <AlertCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'disputed': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  const formatTime = (dateString) => {
    return format(new Date(dateString), 'h:mm a');
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <img
            src={booking.caregiver?.avatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'}
            alt={booking.caregiver?.name}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div>
            <h3 className="font-semibold text-gray-900">{booking.caregiver?.name}</h3>
            <p className="text-sm text-gray-600">{booking.caregiver?.title}</p>
            {booking.invoiceNumber && (
              <div className="flex items-center gap-1 mt-1">
                <Hash className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-500 font-mono">{booking.invoiceNumber}</span>
              </div>
            )}
          </div>
        </div>
        <div className={`badge ${getStatusColor(booking.status)} flex items-center gap-1`}>
          {getStatusIcon(booking.status)}
          {booking.status.replace('_', ' ')}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <div>
            <p className="text-sm text-gray-500">Start Date</p>
            <p className="text-sm font-medium">{formatDate(booking.start)}</p>
            <p className="text-xs text-gray-500">{formatTime(booking.start)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <div>
            <p className="text-sm text-gray-500">End Date</p>
            <p className="text-sm font-medium">{formatDate(booking.end)}</p>
            <p className="text-xs text-gray-500">{formatTime(booking.end)}</p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <div>
            <p className="text-sm text-gray-500">Duration</p>
            <p className="text-sm font-medium">{booking.durationHours} hours</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gray-400" />
          <div>
            <p className="text-sm text-gray-500">Pickup</p>
            <p className="text-sm font-medium capitalize">{booking.pickupType}</p>
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-500">Pet</p>
          <p className="text-sm font-medium">{booking.pet?.name} ({booking.pet?.species})</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Amount</p>
          <p className="text-lg font-semibold text-gray-900">₹{booking.escrowAmount}</p>
        </div>
      </div>
      
      {booking.notes && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">{booking.notes}</p>
        </div>
      )}

      {booking.tags && booking.tags.length > 0 && (
        <div className="mb-4">
          <TagDisplay tags={booking.tags} maxTags={4} size="xs" color="auto" showCount={true} />
        </div>
      )}
      
      {showActions && (
        <div className="flex gap-2">
          {booking.status === 'requested' && (
            <>
              <button className="btn-outline flex-1">Cancel</button>
              <button className="btn-secondary flex-1">Message</button>
            </>
          )}
          {booking.status === 'accepted' && (
            <>
              <button className="btn-outline flex-1">View Details</button>
              <button className="btn-primary flex-1">Message</button>
            </>
          )}
          {booking.status === 'in_progress' && (
            <>
              <button className="btn-outline flex-1">Track</button>
              <button className="btn-primary flex-1">Message</button>
            </>
          )}
          {booking.status === 'completed' && (
            <>
              <button className="btn-outline flex-1">View Receipt</button>
              <button className="btn-secondary flex-1">Leave Review</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingCard;
